import {addDays, closestTo, differenceInCalendarDays} from 'date-fns';

import {getLockDate} from '../../../../../util/time-registration/time-locking/TimeLockingDate';
import type {TimeRegistrationModal_viewer$data as TimeRegistrationModal_viewer} from '../modal-content/__generated__/TimeRegistrationModal_viewer.graphql';
import {TimeRegistrationModal_entity} from '../types/TimeRegistrationModalEntity';
import {TimeRegistrationEntity} from '../types/TimeRegistrationEntityId';
import {
	isTimeRegistrationDateInvalid,
	isTimeRegistrationDateInvalidWithReason,
} from '../../../../../util/time-registration/time-registration-settings/TimeRegistrationDateFilter';
import {createDateFromNullableYMD} from '../../../../../util/DateUtil';

export function getLockDateFromViewer(viewer: TimeRegistrationModal_viewer): Date | null {
	if (viewer.company && viewer.company.person) {
		const {lockedPeriodYear, lockedPeriodMonth, lockedPeriodDay, person} = viewer.company;
		const {submitLockedDateYear, submitLockedDateMonth, submitLockedDateDay, excludeFromCompanyLockedPeriod} = person;

		return getLockDate({
			companyLockYear: lockedPeriodYear,
			companyLockMonth: lockedPeriodMonth,
			companyLockDay: lockedPeriodDay,
			personLockYear: submitLockedDateYear,
			personLockMonth: submitLockedDateMonth,
			personLockDay: submitLockedDateDay,
			personExcludedFromCompanyLock: excludeFromCompanyLockedPeriod,
		});
	}

	return null;
}

type DateDisabledFromTargetEntityInput = {
	date: Date;
	targetEntity: TimeRegistrationModal_entity | null;
	ignoredDates?: Date[];
};
export function getIsDateDisabledFromTargetEntity(input: DateDisabledFromTargetEntityInput, withReason?: true): string | null;
export function getIsDateDisabledFromTargetEntity(input: DateDisabledFromTargetEntityInput, withReason?: false): boolean;
export function getIsDateDisabledFromTargetEntity(
	{date, targetEntity, ignoredDates}: DateDisabledFromTargetEntityInput,
	withReason = false
): boolean | string | null {
	if (targetEntity) {
		const timeRegistrationDateValidator = withReason
			? isTimeRegistrationDateInvalidWithReason
			: isTimeRegistrationDateInvalid;

		switch (targetEntity.entity) {
			case TimeRegistrationEntity.Task: {
				const taskStartDate = createDateFromNullableYMD({
					year: targetEntity.startYear,
					month: targetEntity.startMonth,
					day: targetEntity.startDay,
				});
				const taskEndDate = createDateFromNullableYMD({
					year: targetEntity.deadlineYear,
					month: targetEntity.deadlineMonth,
					day: targetEntity.deadlineDay,
				});
				const taskProject = targetEntity.project;
				const projectStartDate = taskProject
					? createDateFromNullableYMD({
							year: taskProject.projectStartYear,
							month: taskProject.projectStartMonth,
							day: taskProject.projectStartDay,
					  })
					: null;
				const projectEndDate = taskProject
					? createDateFromNullableYMD({
							year: taskProject.projectEndYear,
							month: taskProject.projectEndMonth,
							day: taskProject.projectEndDay,
					  })
					: null;

				return timeRegistrationDateValidator({
					date,
					taskStartDate,
					taskEndDate,
					projectStartDate,
					projectEndDate,
					ignoredDates,
				});
			}
			case TimeRegistrationEntity.Project: {
				const projectStartDate = createDateFromNullableYMD({
					year: targetEntity.projectStartYear,
					month: targetEntity.projectStartMonth,
					day: targetEntity.projectStartDay,
				});
				const projectEndDate = createDateFromNullableYMD({
					year: targetEntity.projectEndYear,
					month: targetEntity.projectEndMonth,
					day: targetEntity.projectEndDay,
				});

				return timeRegistrationDateValidator({date, projectStartDate, projectEndDate, ignoredDates});
			}
		}
	}

	return withReason ? null : false;
}

function noNulls<T>(array: (T | null)[]): T[] {
	return array.filter((item): item is T => item !== null);
}
export function getClosestValidTimeRegDateForTargetEntity(targetEntity: TimeRegistrationModal_entity | null): Date | null {
	if (targetEntity) {
		switch (targetEntity.entity) {
			case TimeRegistrationEntity.Task: {
				const taskStartDate = createDateFromNullableYMD({
					year: targetEntity.startYear,
					month: targetEntity.startMonth,
					day: targetEntity.startDay,
				});
				const taskEndDate = createDateFromNullableYMD({
					year: targetEntity.deadlineYear,
					month: targetEntity.deadlineMonth,
					day: targetEntity.deadlineDay,
				});
				return closestTo(new Date(), noNulls([taskStartDate, taskEndDate]) as Date[]) || null;
			}
			case TimeRegistrationEntity.Project: {
				const projectStartDate = createDateFromNullableYMD({
					year: targetEntity.projectStartYear,
					month: targetEntity.projectStartMonth,
					day: targetEntity.projectStartDay,
				});
				const projectEndDate = createDateFromNullableYMD({
					year: targetEntity.projectEndYear,
					month: targetEntity.projectEndMonth,
					day: targetEntity.projectEndDay,
				});
				return closestTo(new Date(), noNulls([projectStartDate, projectEndDate]) as Date[]) || null;
			}
		}
	}

	return null;
}

export function getInitialOrFirstNonLockValidDateForTargetEntity(
	initialDate: Date,
	lockDate: Date | null,
	targetEntity: TimeRegistrationModal_entity | null
): Date {
	const initialOrNonLockedDate =
		lockDate && differenceInCalendarDays(lockDate, initialDate) >= 0 ? addDays(lockDate, 1) : initialDate;

	if (
		getIsDateDisabledFromTargetEntity({
			date: initialDate,
			targetEntity,
		})
	) {
		const endDate = getClosestValidTimeRegDateForTargetEntity(targetEntity);
		if (endDate) {
			return endDate;
		}
	}

	return initialOrNonLockedDate;
}
